import './App.css';
import { useNavigate, useHref, useLocation } from "react-router-dom";
import { NextUIProvider } from "@nextui-org/react";

import NavBar from './pages/components/navbar/navbar';
import Particles from './pages/components/particles';
import Home from './pages/home/home';
import Docs from './pages/docs/docs';
import Kbar from './pages/components/kbar';
import { useEffect, useState, Suspense, lazy } from 'react';
import { pages } from './pages/docs/pages/pages';
import { KBarProvider } from 'kbar';

import { actions } from './pages/components/kbar';
import CookieConsentBanner from './pages/components/cookiesbanner';
import MaintenanceGate from './pages/components/maintenance/maintenanceGate';
import AchievementUnlockPopup from './pages/components/achievementUnlockPopup';
import { trackPageview } from './utils/analytics';

const PrivacyPolicy = lazy(() => import('./pages/privacy/privacy'));
const TermsOfServices = lazy(() => import('./pages/tos/tos'));
const StatusPage = lazy(() => import('./pages/status/status'));
const ChangelogPage = lazy(() => import('./pages/changelog/changelog'));
const DownloadAppPage = lazy(() => import('./pages/download/download'));
const SupportPage = lazy(() => import('./pages/support/support'));
const LearningPage = lazy(() => import('./pages/learning/learning'));
const EnterprisePage = lazy(() => import('./pages/enterprise/enterprise'));
const PackagesPage = lazy(() => import('./pages/packages/packages'));
const NotFoundPage = lazy(() => import('./pages/errors/notfound'));

const LoginPage = lazy(() => import('./pages/auth/login'));
const RegisterPage = lazy(() => import('./pages/auth/register'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/forgotPassword'));
const ProfileSettingsPage = lazy(() => import('./pages/profile/profileSettings'));
const PublicProfilePage = lazy(() => import('./pages/profile/publicProfile'));
const BlogHubPage = lazy(() => import('./pages/blog/blogHub'));
const BlogEditorPage = lazy(() => import('./pages/blog/blogEditor'));
const BlogPostPage = lazy(() => import('./pages/blog/blogPost'));
const ForumHomePage = lazy(() => import('./pages/forum/forumHome'));
const ForumCategoryPage = lazy(() => import('./pages/forum/forumCategory'));
const ForumNewThreadPage = lazy(() => import('./pages/forum/newThread'));
const ForumThreadPage = lazy(() => import('./pages/forum/forumThread'));
const AnnouncementPage = lazy(() => import('./pages/announcements/announcementPage'));
const AdminUsersPage = lazy(() => import('./pages/admin/adminUsers'));
const AdminAnnouncementsPage = lazy(() => import('./pages/admin/adminAnnouncements'));
const AdminMaintenancePage = lazy(() => import('./pages/admin/adminMaintenance'));
const AdminBadgesPage = lazy(() => import('./pages/admin/adminBadges'));
const AdminSponsorsPage = lazy(() => import('./pages/admin/adminSponsors'));
const AdminAnalyticsPage = lazy(() => import('./pages/admin/adminAnalytics'));
const ApisPage = lazy(() => import('./pages/apis/apis'));

const GA_MEASUREMENT_ID = 'G-KPVZX13YJ4';

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedLink, setSelectedLink] = useState({ page: pages[0].title, link: pages[0].links[0] });

  const [hasCookieConsent, setHasCookieConsent] = useState(false);

  useEffect(() => {
    trackPageview(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    const consent = localStorage.getItem('cookie_consent_accepted');
    if (consent === 'true') {
      setHasCookieConsent(true);
    } else {
      setHasCookieConsent(false);
    }

    const match = location.pathname.match(/^\/docs\/([^/]+)\/([^/]+)$/);
    if (match) {
      // eslint-disable-next-line no-unused-vars
      const [_, page, link] = match;

      const matchedPage = pages.find(p => p.title.replaceAll(" ", "-").toLowerCase() === page.toLowerCase());
      const matchedLink = matchedPage?.links.find(l => l.replaceAll(" ", "-").toLowerCase() === link.toLowerCase());

      if (matchedPage && matchedLink) {
        setSelectedLink({ page: matchedPage.title, link: matchedLink.replaceAll(" ", "_") });
      }
    }
  }, [location.pathname]);

  useEffect(() => {
    if (hasCookieConsent && GA_MEASUREMENT_ID && GA_MEASUREMENT_ID !== 'G-337952744') {
      // Prevent duplicate script loading
      if (window.gtag) {
        // If gtag is already defined, it means the script has been loaded.
        // We can just send the page view event.
        window.gtag('config', GA_MEASUREMENT_ID, {
          'anonymize_ip': true,
          'page_path': location.pathname + location.search
        });
        window.gtag('event', 'page_view', {
          'page_location': window.location.href,
          'page_path': location.pathname + location.search,
          'page_title': document.title,
          'send_to': GA_MEASUREMENT_ID
        });
        return;
      }

      const script = document.createElement('script');
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
      script.async = true;
      document.head.appendChild(script);

      script.onload = () => {
        if (!window.dataLayer) {
          window.dataLayer = [];
        }
        if (!window.gtag) {
          function gtag(){window.dataLayer.push(arguments);}
          window.gtag = gtag;
        }
        window.gtag('js', new Date());
        window.gtag('config', GA_MEASUREMENT_ID, {
          'anonymize_ip': true,
          'page_path': location.pathname + location.search
        });

        window.gtag('event', 'page_view', {
          'page_location': window.location.href,
          'page_path': location.pathname + location.search,
          'page_title': document.title,
          'send_to': GA_MEASUREMENT_ID
        });
      };

      return () => {
        if (script && document.head.contains(script)) {
          document.head.removeChild(script);
        }
      };
    }
  }, [hasCookieConsent, location.pathname, location.search]);

  return (
    <NextUIProvider navigate={navigate} useHref={useHref} className="overflow-auto h-[100vh] appContainer">
      <MaintenanceGate>
        <KBarProvider
          actions={actions}
          options={
            {
              disableScrollbarManagement: true,
              enableHistory: true,
            }
          }>
          <Kbar setSelectedLink={setSelectedLink} />
          <Particles
            className="fixed inset-0 z-[0]"
            quantity={60}
            ease={80}
            color='#fff'
            refresh
            size={1}
          />
          <NavBar selectedLink={selectedLink} setSelectedLink={setSelectedLink} />
          <div className='relative'>
            <div className={`${location.pathname === '/' ? 'block' : 'hidden overflow-hidden max-h-full'} absolute w-full`}>
              <Home />
            </div>
            <div className={`${location.pathname.startsWith('/docs') ? 'block' : 'hidden overflow-hidden max-h-full'} absolute w-full`}>
              <Docs selectedLink={selectedLink} setSelectedLink={setSelectedLink} />
            </div>
            <Suspense fallback={null}>
              {location.pathname.startsWith('/privacy') && (
                <div className="absolute w-full">
                  <PrivacyPolicy />
                </div>
              )}
              {location.pathname.startsWith('/tos') && (
                <div className="absolute w-full">
                  <TermsOfServices />
                </div>
              )}
              {location.pathname.startsWith('/status') && (
                <div className="absolute w-full">
                  <StatusPage />
                </div>
              )}
              {location.pathname.startsWith('/download') && (
                <div className="absolute w-full">
                  <DownloadAppPage />
                </div>
              )}
              {location.pathname.startsWith('/support') && (
                <div className="absolute w-full">
                  <SupportPage />
                </div>
              )}
              {location.pathname.startsWith('/learning') && (
                <div className="absolute w-full">
                  <LearningPage />
                </div>
              )}
              {location.pathname.startsWith('/enterprise') && (
                <div className="absolute w-full">
                  <EnterprisePage />
                </div>
              )}
              {location.pathname.startsWith('/packages') && (
                <div className="absolute w-full">
                  <PackagesPage />
                </div>
              )}
              {location.pathname === '/login' && (
                <div className="absolute w-full">
                  <LoginPage />
                </div>
              )}
              {location.pathname === '/register' && (
                <div className="absolute w-full">
                  <RegisterPage />
                </div>
              )}
              {location.pathname === '/forgot-password' && (
                <div className="absolute w-full">
                  <ForgotPasswordPage />
                </div>
              )}
              {location.pathname.startsWith('/settings/profile') && (
                <div className="absolute w-full">
                  <ProfileSettingsPage />
                </div>
              )}
              {location.pathname.startsWith('/@') && (
                <div className="absolute w-full">
                  <PublicProfilePage />
                </div>
              )}
              {location.pathname === '/blog' && (
                <div className="absolute w-full">
                  <BlogHubPage />
                </div>
              )}
              {(location.pathname === '/blog/new' || /^\/blog\/edit\/\d+$/.test(location.pathname)) && (
                <div className="absolute w-full">
                  <BlogEditorPage />
                </div>
              )}
              {location.pathname.startsWith('/blog/') && location.pathname !== '/blog/new' && !/^\/blog\/edit\/\d+$/.test(location.pathname) && (
                <div className="absolute w-full">
                  <BlogPostPage />
                </div>
              )}
              {location.pathname === '/forum' && (
                <div className="absolute w-full">
                  <ForumHomePage />
                </div>
              )}
              {/^\/forum\/thread\/\d+$/.test(location.pathname) && (
                <div className="absolute w-full">
                  <ForumThreadPage />
                </div>
              )}
              {/^\/forum\/[^/]+\/new$/.test(location.pathname) && (
                <div className="absolute w-full">
                  <ForumNewThreadPage />
                </div>
              )}
              {location.pathname.startsWith('/forum/') && !/^\/forum\/thread\/\d+$/.test(location.pathname) && !/^\/forum\/[^/]+\/new$/.test(location.pathname) && (
                <div className="absolute w-full">
                  <ForumCategoryPage />
                </div>
              )}
              {location.pathname.startsWith('/admin') && (
                <div className="absolute w-full">
                  {location.pathname.startsWith('/admin/announcements') ? <AdminAnnouncementsPage />
                    : location.pathname.startsWith('/admin/maintenance') ? <AdminMaintenancePage />
                    : location.pathname.startsWith('/admin/badges') ? <AdminBadgesPage />
                    : location.pathname.startsWith('/admin/sponsors') ? <AdminSponsorsPage />
                    : location.pathname.startsWith('/admin/users') ? <AdminUsersPage />
                    : <AdminAnalyticsPage />}
                </div>
              )}
              {/^\/announcements\/\d+$/.test(location.pathname) && (
                <div className="absolute w-full">
                  <AnnouncementPage />
                </div>
              )}
              {location.pathname.startsWith('/apis') && (
                <div className="absolute w-full">
                  <ApisPage />
                </div>
              )}
              {!['/','/docs','/privacy','/tos','/status','/download','/support','/changelog','/learning','/enterprise','/packages','/login','/register','/forgot-password','/settings','/blog','/forum','/admin','/apis','/@','/announcements'].some(p => location.pathname === p || (p !== '/' && location.pathname.startsWith(p))) && (
                <div className="absolute w-full">
                  <NotFoundPage />
                </div>
              )}
              {location.pathname.startsWith('/changelog') && (
                <div className="absolute w-full">
                  <ChangelogPage />
                </div>
              )}
            </Suspense>
          </div>
          <CookieConsentBanner />
          <AchievementUnlockPopup />
        </KBarProvider>
      </MaintenanceGate>
    </NextUIProvider>
  );
}

export default App;
